import { call, select } from 'redux-saga/effects'
import { LocationService } from '../../../services/locations.service'
import { ILocation, Locations } from '../../../services/globalIndexDb.service'
import { ICity, ICountyOption, IOption } from '../../../common/common.types'
import { IPatientDemographicsOptionsState } from '../../options/store/options.types'
import { getPatientDemographicsOptionsSelector } from '../../options/selectors'
import Logger from '../../../services/logger.service'
import { normalizeCity } from '../../../utilities/normalizeCity.util'

export interface ILocationInfo {
  city: ICity | null
  state: IOption | null
  county: ICountyOption | null
}

export interface ILocationNemsisLookupData extends ILocationInfo {
  cityError: string
  stateError: string
  countyError: string
}

export function* getCityNemsisCode(locationInfo: ILocationInfo) {
  const patientDemographicsOptions: IPatientDemographicsOptionsState = yield select(
    getPatientDemographicsOptionsSelector
  )
  const { city, state, county } = locationInfo
  const responseData: ILocationNemsisLookupData = {
    cityError: '',
    stateError: '',
    countyError: '',
    city: null,
    state: null,
    county: null,
  }
  try {
    if (!state || !state.id || !city || !city.name) {
      return responseData
    }

    const cityName = city?.name?.trim().toLowerCase()

    const normalizedCityName = normalizeCity(cityName)
    const stateId = state.id
    const countyId = county?.id
    let resultCount = 0
    let locationResponse: ILocation | null = null
    const locationService = new LocationService()

    if (countyId) {
      const id = `${normalizedCityName}_${stateId}_${countyId}`
      locationResponse = yield call([locationService, locationService.getLocation], id)
      resultCount = locationResponse ? 1 : 0
    } else {
      const locationsData: Locations = yield call(
        [locationService, locationService.getLocationByCityState],
        normalizedCityName,
        stateId.toString()
      )
      if (locationsData && locationsData.length > 0) {
        resultCount = locationsData.length
        if (locationsData.length === 1) locationResponse = locationsData[0]
      }
    }
    return buildSearchResultData(locationInfo, locationResponse, resultCount, patientDemographicsOptions, responseData)
  } catch (e) {
    Logger.error(`Error getCityNemsisCode', ${e}`)
  }

  function buildSearchResultData(
    locationRequest: ILocationInfo,
    locationResponse: ILocation | null,
    resultCount: number,
    patientDemographicsOptions: IPatientDemographicsOptionsState,
    responseData: ILocationNemsisLookupData
  ) {
    const { stateOptions, patientCountyOptions } = patientDemographicsOptions
    if (resultCount > 1) {
      responseData.countyError = 'County is required to uniquely identify this address'
      return responseData
    }
    if (!locationResponse || resultCount === 0) {
      if (locationRequest.city && locationRequest.state && locationRequest.county)
        responseData.cityError = 'City/State/County Combination is invalid'
      else responseData.cityError = 'City/State Combination is invalid'
      return responseData
    }
    responseData.city = {
      name: locationResponse.city,
      nemsisCode: locationResponse.nemsisCode,
    }
    const stateOption = stateOptions.find((option) => option.id?.toString() === locationResponse.state.toString())
    if (stateOption && stateOption.name) {
      responseData.state = {
        id: stateOption.id,
        name: stateOption?.name,
      }
    }
    const countyOption = patientCountyOptions.find(
      (option) => option.name.toLocaleLowerCase() === locationResponse.county.toLocaleLowerCase()
    )
    if (countyOption) {
      responseData.county = {
        id: countyOption.id,
        name: countyOption.name,
        nemsisCode: countyOption.nemsisCode,
        state: state?.id?.toString() || '',
      }
    }
    return responseData
  }
}
